// Single Responsibility: Display AI Analysis Results
import React from 'react';
import { Target, Sparkles, AlertTriangle, Lightbulb } from 'lucide-react';
import { AIDecompositionResponse } from '../../types/ai';
import { AIGoalCard } from './AIGoalCard';

interface AIResultsDisplayProps {
  decomposition: AIDecompositionResponse;
  onAccept: () => void;
  onBack: () => void;
  onClose: () => void;
  accepting: boolean;
}

export const AIResultsDisplay: React.FC<AIResultsDisplayProps> = ({
  decomposition,
  onAccept,
  onBack,
  onClose,
  accepting
}) => {
  const { generatedStructure } = decomposition;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">AI Analysis Results</h3>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Confidence:</span>
          <span className="font-medium text-green-600">
            {Math.round(decomposition.confidenceScore * 100)}%
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Generated Goals */}
        <div className="card">
          <div className="flex items-center gap-2 mb-4">
            <Target className="w-5 h-5 text-blue-600" />
            <h4 className="font-semibold text-gray-900">Generated Goals</h4>
          </div>
          <div className="space-y-3">
            {generatedStructure.goals.slice(0, 3).map((goal, index) => (
              <AIGoalCard key={index} goal={goal} index={index} />
            ))}
            {generatedStructure.goals.length > 3 && (
              <p className="text-sm text-gray-600 text-center">
                +{generatedStructure.goals.length - 3} more goals
              </p>
            )}
            {generatedStructure.goals.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">
                No goals generated
              </p>
            )}
          </div>
        </div>

        {/* Standalone Opportunities */}
        <div className="card">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-amber-600" />
            <h4 className="font-semibold text-gray-900">Standalone Opportunities</h4>
          </div>
          <div className="space-y-3">
            {generatedStructure.standaloneOpportunities.map((opp, index) => (
              <div key={index} className="p-3 bg-amber-50 rounded-lg">
                <h5 className="font-medium text-gray-900">{opp.name}</h5>
                <p className="text-sm text-amber-700 mt-1">{opp.estimatedRevenue}</p>
                <p className="text-xs text-gray-600 mt-1">{opp.reason}</p>
              </div>
            ))}
            {generatedStructure.standaloneOpportunities.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">
                No standalone opportunities identified
              </p>
            )}
          </div>
        </div>

        {/* Risk Factors */}
        <div className="card">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <h4 className="font-semibold text-gray-900">Risk Factors</h4>
          </div>
          <div className="space-y-3">
            {generatedStructure.risks.map((risk, index) => (
              <div key={index} className="p-3 bg-red-50 rounded-lg">
                <h5 className="font-medium text-gray-900">{risk.risk}</h5>
                <p className="text-sm text-red-700 mt-1">
                  Impact: {risk.impact} | Probability: {Math.round(risk.probability * 100)}%
                </p>
                <p className="text-xs text-gray-600 mt-1">{risk.mitigation}</p>
              </div>
            ))}
            {generatedStructure.risks.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">
                No risks identified
              </p>
            )}
          </div>
        </div>

        {/* Recommendations */}
        <div className="card">
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb className="w-5 h-5 text-green-600" />
            <h4 className="font-semibold text-gray-900">Recommendations</h4>
          </div>
          <div className="space-y-2">
            {generatedStructure.recommendations.map((rec, index) => (
              <div key={index} className="p-3 bg-green-50 rounded-lg">
                <p className="text-sm text-gray-900">{rec}</p>
              </div>
            ))}
            {generatedStructure.recommendations.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">
                No recommendations provided
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
        <button onClick={onBack} className="btn-secondary">
          Back to Edit
        </button>
        <button onClick={onClose} className="btn-secondary">
          Save for Later
        </button>
        <button
          onClick={onAccept}
          disabled={accepting}
          className="btn-primary flex items-center gap-2"
        >
          <Target className="w-4 h-4" />
          {accepting ? 'Creating Goals...' : 'Accept & Create Goals'}
        </button>
      </div>
    </div>
  );
};